import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, StampIcon, Mail, Wallet, Luggage, Plane, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { openInquiryPopup } from '../lib/contact';

const steps = [
  {
    n: '01',
    icon: ClipboardList,
    title: 'Fill In The Application Form',
    desc: 'Select which university to opt for your MBBS in Russia and take the first step by filling up the application form.',
  },
  {
    n: '02',
    icon: StampIcon,
    title: 'Get Your Visa',
    desc: 'Complete all your documents and other add-ons to get your VISA and commence to your new phase of life.',
  },
  {
    n: '03',
    icon: Mail,
    title: 'Get Your Admission Letter',
    desc: 'Receive your admission letter from the university and start with packing your bags to your destination.',
  },
  {
    n: '04',
    icon: Wallet,
    title: 'Pay Your Tuition Fees',
    desc: 'Check out the loan if required, and clear all your dues to officially confirm your enrollment to the university.',
  },
  {
    n: '05',
    icon: Luggage,
    title: 'Pack Your Suitcase',
    desc: 'Grab up your favorite suitcase and dresses and finish up your packing. Remember to keep the thermals!',
  },
  {
    n: '06',
    icon: Plane,
    title: 'Welcome To Study in Russia!',
    desc: 'Board your flights to Russia with us and start your journey to make your dream a reality with MBBS in Russia.',
  },
];

export default function AdmissionProcess() {
  return (
    <div className="bg-white" data-testid="admission-process-page">
      <SEO
        title="Admission Process — MBBS in Russia | 6-Step Guide | Russian Education"
        description="The simplest 6-step admission procedure for MBBS in Russia — from application form to boarding your flight. End-to-end guided by Russian Education counsellors."
        path="/admission-process"
        keywords="MBBS in Russia admission process, how to apply MBBS Russia, Russia MBBS application, MBBS Russia visa"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55z'/%3E%3C/svg%3E\")",
            backgroundSize: '160px',
          }}
        />
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative">
          <div className="text-white max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/logo.jpg"
                alt="Russian Education"
                className="w-16 h-16 rounded-full border-2 border-white/40 bg-white p-1"
              />
              <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">
                admission process<br />step-by-step guide
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">
              ADMISSION<br />PROCESS
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/90 max-w-3xl leading-relaxed">
              Simplest admission procedure for abroad — a clean 6-step journey from your first
              application form to the day you board your flight to your dream Russian medical
              university.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={openInquiryPopup}
                data-testid="talk-counsellor-btn"
                className="bg-brand hover:bg-brand-dark text-white font-bold h-12 px-8 rounded-full btn-shift"
              >
                Talk to a Counsellor
              </Button>
              <Link
                to="/universities"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/10 border border-white/30 text-white font-bold hover:bg-white/20 transition-colors"
                data-testid="see-universities-link"
              >
                See Universities <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro line */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-brand font-bold">
            Admission Process
          </div>
          <h2 className="mt-3 font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-primary leading-[1.05] max-w-5xl">
            Simplest Admission Procedure for Abroad
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Fly to your dream destination to pursue your dream career. Follow these six clear steps
            and our counsellors will walk with you at every stage — from filling the form to landing
            in Russia.
          </p>
        </div>
      </section>

      {/* Steps Timeline */}
      <section className="pb-6 md:pb-10">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="relative">
            {/* Vertical timeline line (desktop) */}
            <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[3px] bg-gradient-to-b from-primary via-brand to-primary rounded-full opacity-60" />

            <div className="flex flex-col gap-10 md:gap-14">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const isLeft = i % 2 === 0;
                return (
                  <div
                    key={s.n}
                    className={`relative md:grid md:grid-cols-2 md:gap-10 items-center ${
                      isLeft ? '' : 'md:[&>*:first-child]:order-2'
                    }`}
                    data-testid={`step-${s.n}`}
                  >
                    {/* Card */}
                    <div className={`${isLeft ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                      <div className="relative bg-white rounded-2xl border border-border shadow-md hover:shadow-xl p-6 md:p-7 card-lift transition-all">
                        <div
                          className={`flex items-center gap-3 mb-3 ${
                            isLeft ? 'md:justify-end' : 'md:justify-start'
                          }`}
                        >
                          <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
                            <Icon className="w-6 h-6" />
                          </div>
                          <div className="font-serif text-4xl md:text-5xl font-extrabold text-primary/15 leading-none select-none">
                            {s.n}
                          </div>
                        </div>
                        <h3 className="font-serif text-xl md:text-2xl font-bold text-primary leading-tight">
                          {s.title}
                        </h3>
                        <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                          {s.desc}
                        </p>
                      </div>
                    </div>

                    {/* Center marker (desktop) */}
                    <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-primary text-white font-serif font-extrabold text-lg items-center justify-center ring-4 ring-white shadow-xl shadow-primary/30 z-10">
                      {s.n}
                    </div>

                    {/* Empty side spacer (holds grid symmetry) */}
                    <div className="hidden md:block" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why choose us / assurance */}
      <section className="py-14 md:py-16 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                t: 'End-to-End Guidance',
                d: 'From form submission to hostel check-in, every step is handled by our senior counsellors.',
              },
              {
                t: '99% Visa Success',
                d: 'Complete document verification and mock interviews ensure a smooth visa approval.',
              },
              {
                t: 'Fly with Us Program',
                d: 'Group departures with our team — you never travel to Russia alone.',
              },
            ].map((c, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-border p-6 shadow-sm card-lift"
              >
                <CheckCircle2 className="w-8 h-8 text-brand mb-3" strokeWidth={2.4} />
                <div className="font-serif font-bold text-lg text-primary">{c.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-primary text-white p-8 md:p-10">
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 15c-6 6-4 14 3 17 6 3 15-2 15-10 0-6-6-12-13-12-2 0-4 2-5 5zm0 30c-14 8-26 25-26 42 0 12 12 24 26 24s26-12 26-24c0-17-12-34-26-42z'/%3E%3C/svg%3E\")",
                backgroundSize: '120px',
              }}
            />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold mb-2">
                  Ready to Begin?
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold leading-tight">
                  Start your MBBS-in-Russia journey today — free counselling &amp; direct admission
                  in NMC-approved universities.
                </h3>
              </div>
              <Button
                onClick={openInquiryPopup}
                data-testid="apply-now-cta"
                className="bg-brand hover:bg-brand-dark text-white font-bold h-12 px-7 rounded-full btn-shift shrink-0"
              >
                Apply Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="py-4 bg-white border-t border-border">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>© 1988 — 2026, Russian Education</div>
          <div>All rights reserved</div>
        </div>
      </div>
    </div>
  );
}
